import { defineStore } from 'pinia'
import { reactive, watch, computed, nextTick } from 'vue'
import { useFetch } from '@/composables'
import type { SvgItem, InitParams, EnsureOpts, RecordEntry } from './types'

export const DEFAULT_TTL_MS = 12 * 60 * 60 * 1000
// Negative Cache（短期缓存“缺失”结果，避免频繁重试）
export const NEGATIVE_TTL_MS = 5 * 60 * 1000
export const COALESCE_WINDOW_MS = 8

type Bucket = { symbols: Set<string>; resolves: Array<() => void>; timer: any | null }

export const useSvgStore = defineStore('svgStore', () => {
  const svgMap = reactive<Map<string, RecordEntry>>(new Map())
  const negativeAt = new Map<string, number>()
  // 按 image_type 进行批次聚合，key = image_type
  const buckets = new Map<string, Bucket>()

  const { data, loading, fetchData } = useFetch<SvgItem[]>({
    url: '/api/symbol/svg_list',
    autoFetch: false,
  })

  function mergeList(list: SvgItem[] | null | undefined) {
    if (!Array.isArray(list) || list.length === 0) return
    const now = Date.now()
    for (const item of list) {
      if (!item || !(item as any).symbol) continue
      svgMap.set((item as any).symbol, { item, updatedAt: now })
    }
  }

  watch(data, (newData) => mergeList(newData))

  let _initPromise: Promise<void> | null = null
  let _lastInitKey = ''
  const toKey = (p?: InitParams) => JSON.stringify(p ?? {})

  // 判定负缓存是否仍然有效
  const isNegativeFresh = (symbol: string, ttlMs = NEGATIVE_TTL_MS) => {
    const ts = negativeAt.get(symbol)
    return !!ts && Date.now() - ts <= ttlMs
  }

  const reset = () => {
    data.value = null
    svgMap.clear()
    _initPromise = null
    _lastInitKey = ''
  }

  const init = async (params?: InitParams) => {
    const key = toKey(params)
    if (_initPromise && key === _lastInitKey) return _initPromise
    _initPromise = (async () => {
      try {
        if (Array.isArray(data.value) && data.value.length > 0) return
        await fetchData(params) // watch(data) 会自动 merge
        _lastInitKey = key
      } catch (e) {
        _initPromise = null
        throw e
      }
    })()
    return _initPromise
  }

  const getCached = (symbol: string): SvgItem | undefined => svgMap.get(symbol)?.item

  const getUpdatedAt = (symbol: string): number | undefined => svgMap.get(symbol)?.updatedAt

  const isStale = (symbol: string, ttlMs = DEFAULT_TTL_MS) => {
    const ts = svgMap.get(symbol)?.updatedAt
    if (!ts) return true
    return Date.now() - ts > ttlMs
  }

  const inflight = new Map<string, Promise<SvgItem | null>>()

  async function fetchBatch(symbols: string[], image_type: string = 'svg') {
    if (symbols.length === 0) return

    // 等待接口返回后，再等待一轮 nextTick，确保 watch(data)→mergeList 已把结果写入 svgMap，消除“先 resolve 后合并”的竞态
    const batchPromise = (async () => {
      await fetchData({ image_type, symbol: symbols })
      await nextTick()
      // 此时 svgMap 已更新，返回每个 symbol 的最新缓存（允许个别缺失为 null）
      return symbols.map((s) => svgMap.get(s)?.item ?? null)
    })()

    // 把批量结果按索引拆分给每个 symbol，确保 resolve 时机在 nextTick 之后
    symbols.forEach((s, idx) => {
      const p = batchPromise.then((arr) => arr[idx] ?? null)
      inflight.set(
        s,
        p.finally(() => inflight.delete(s)),
      )
    })

    // 调用方通常不单独等每个 symbol，但等待整批结束可保证 svgMap/inflight 状态一致
    await batchPromise
  }

  async function ensureSvg(symbol: string, opts?: EnsureOpts): Promise<SvgItem | null> {
    const ttl = opts?.ttlMs ?? DEFAULT_TTL_MS
    const entry = svgMap.get(symbol)

    if (entry && !isStale(symbol, ttl)) return entry.item

    const inq = inflight.get(symbol)
    if (inq) return inq

    await fetchBatch([symbol], opts?.image_type)
    return svgMap.get(symbol)?.item ?? null
  }

  async function ensureSvgs(
    symbols: string[],
    opts?: EnsureOpts,
  ): Promise<Map<string, SvgItem | null>> {
    const ttl = opts?.ttlMs ?? DEFAULT_TTL_MS
    const needFetch: string[] = []

    // 不存在/过期且不在 in-flight 的才纳入本次请求
    for (const symbol of symbols) {
      const stale = isStale(symbol, ttl)
      const hasInflight = inflight.has(symbol)
      if (stale && !hasInflight) needFetch.push(symbol)
    }

    if (needFetch.length) {
      await fetchBatch(needFetch, opts?.image_type)
    }

    // 逐 symbol 构造等待项，保证与 symbols 一一对齐；非在途则用当前缓存兜底
    const waits = symbols.map(
      (s) => inflight.get(s) ?? Promise.resolve(svgMap.get(s)?.item ?? null),
    )
    const results = await Promise.all(waits)

    // 按 symbols 顺序产出结果 Map（值为 SvgItem|null）
    const map = new Map<string, SvgItem | null>()
    symbols.forEach((s, i) => map.set(s, results[i]))
    return map
  }

  const size = computed(() => svgMap.size)
  const has = (symbol: string) => svgMap.has(symbol)
  const entries = computed(() => Array.from(svgMap.entries()))
  const values = computed(() => Array.from(svgMap.values()).map((e) => e.item))

  return {
    loading,
    size,
    entries,
    values,
    svgMap,

    init,
    reset,

    getCached,
    getUpdatedAt,
    has,
    isStale,

    ensureSvg,
    ensureSvgs,

    data,
    fetchData,
  }
})

export default useSvgStore
